import React, { useState } from 'react';
import { X, Plus, Trash2, Copy, Save, Pencil, KeyRound, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import { membersAPI } from '../services/apiService';

export const PRESET_PORTALS = ['CPGRAMS', 'DPG', 'PFG', 'MAIL', 'Other'];

const blankPortal = () => ({
  name: 'CPGRAMS',
  custom_name: '',
  url: '',
  username: '',
  password: ''
});

const displayName = (p) => (p.name === 'Other' ? (p.custom_name || 'Other') : p.name);

const PortalFormPopup = ({ initial, onClose, onSave }) => {
  const [form, setForm] = useState(() => ({ ...blankPortal(), ...initial }));

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.url && !form.username && !form.password && !(form.name === 'Other' && form.custom_name)) {
      toast.error('Enter at least URL, username, or password');
      return;
    }
    onSave({
      name: form.name || 'Other',
      custom_name: form.name === 'Other' ? (form.custom_name || '') : '',
      url: form.url || '',
      username: form.username || '',
      password: form.password || ''
    });
  };

  return (
    <div className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md flex flex-col">
        <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-base font-semibold text-slate-900">
              {initial ? 'Edit portal' : 'Add portal'}
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">Portal name, URL, username and password</p>
          </div>
          <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-3">
          <div>
            <label className="field-label">Portal</label>
            <select
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              {PRESET_PORTALS.map((name) => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
          {form.name === 'Other' && (
            <div>
              <label className="field-label">Custom name</label>
              <input
                value={form.custom_name || ''}
                onChange={(e) => setField('custom_name', e.target.value)}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
                placeholder="Portal name"
              />
            </div>
          )}
          <div>
            <label className="field-label">URL</label>
            <input
              value={form.url || ''}
              onChange={(e) => setField('url', e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              placeholder="https://…"
            />
          </div>
          <div>
            <label className="field-label">Username</label>
            <input
              value={form.username || ''}
              onChange={(e) => setField('username', e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label className="field-label">Password</label>
            <input
              value={form.password || ''}
              onChange={(e) => setField('password', e.target.value)}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="btn-secondary">Cancel</button>
            <button type="submit" className="btn-primary inline-flex items-center">
              <Save className="w-4 h-4 mr-1" /> Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

/**
 * Combined Portals Access: row-wise list + separate Add/Edit popup.
 */
const PortalsAccessModal = ({ member, onClose, onSaved }) => {
  const [portals, setPortals] = useState(() => {
    const existing = Array.isArray(member.portal_credentials) ? member.portal_credentials : [];
    return existing.map((p) => ({ ...blankPortal(), ...p }));
  });
  const [saving, setSaving] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);

  const persist = async (nextList, { closeAfter = false } = {}) => {
    setSaving(true);
    try {
      const cleaned = nextList
        .map((p) => ({
          name: p.name || 'Other',
          custom_name: p.name === 'Other' ? (p.custom_name || '') : '',
          url: p.url || '',
          username: p.username || '',
          password: p.password || ''
        }))
        .filter((p) => p.url || p.username || p.password || p.custom_name);

      await membersAPI.update(member.id, {
        ...member,
        portal_credentials: cleaned
      });
      setPortals(cleaned);
      onSaved?.(cleaned);
      toast.success('Portals updated');
      if (closeAfter) onClose();
    } catch (error) {
      console.error(error);
      toast.error('Failed to save portals');
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (text, okMsg) => {
    if (!text) {
      toast.error('Nothing to copy');
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success(okMsg);
    } catch {
      toast.error('Unable to copy');
    }
  };

  const formatOne = (p) => [
    `Portal: ${displayName(p)}`,
    p.url ? `URL: ${p.url}` : null,
    p.username ? `Username: ${p.username}` : null,
    p.password ? `Password: ${p.password}` : null
  ].filter(Boolean).join('\n');

  const copyAll = () => {
    const text = portals.map(formatOne).filter(Boolean).join('\n\n');
    copyText(text, 'All credentials copied');
  };

  const handleFormSave = async (portal) => {
    const next = [...portals];
    if (editIndex == null) next.push(portal);
    else next[editIndex] = portal;
    setFormOpen(false);
    setEditIndex(null);
    await persist(next);
  };

  const handleDelete = async (index) => {
    if (!window.confirm(`Delete ${displayName(portals[index])}?`)) return;
    const next = portals.filter((_, i) => i !== index);
    await persist(next);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">
          <div className="flex items-start justify-between px-5 py-4 border-b border-slate-200">
            <div>
              <h3 className="text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
                <KeyRound className="w-5 h-5 text-primary-600" />
                Portals Access
              </h3>
              <p className="text-xs text-slate-500 mt-0.5">
                {member.name} · UAN {member.uan}
              </p>
            </div>
            <button type="button" onClick={onClose} className="text-slate-400 hover:text-slate-600">
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-slate-100 bg-slate-50/80 shrink-0">
            <p className="text-sm text-slate-600">
              {portals.length} portal{portals.length === 1 ? '' : 's'} saved
            </p>
            <div className="flex gap-2">
              <button type="button" onClick={copyAll} className="btn-secondary inline-flex items-center text-sm" disabled={!portals.length}>
                <Copy className="w-4 h-4 mr-1" /> Copy all
              </button>
              <button
                type="button"
                onClick={() => { setEditIndex(null); setFormOpen(true); }}
                className="btn-primary inline-flex items-center text-sm"
              >
                <Plus className="w-4 h-4 mr-1" /> Add
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-5">
            {portals.length === 0 ? (
              <div className="text-center py-12 border border-dashed border-slate-200 rounded-lg">
                <KeyRound className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500 mb-3">No portals saved yet</p>
                <button
                  type="button"
                  onClick={() => { setEditIndex(null); setFormOpen(true); }}
                  className="btn-primary inline-flex items-center text-sm"
                >
                  <Plus className="w-4 h-4 mr-1" /> Add portal
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 bg-slate-50 border-b border-slate-200">
                      <th className="px-3 py-2.5 whitespace-nowrap">Portal</th>
                      <th className="px-3 py-2.5">URL</th>
                      <th className="px-3 py-2.5 whitespace-nowrap">Username</th>
                      <th className="px-3 py-2.5 whitespace-nowrap">Password</th>
                      <th className="px-3 py-2.5 text-right whitespace-nowrap">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {portals.map((p, index) => (
                      <tr key={`${displayName(p)}-${index}`} className="border-b border-slate-100 hover:bg-violet-50/40">
                        <td className="px-3 py-2.5 font-medium text-slate-900 whitespace-nowrap">
                          {displayName(p)}
                        </td>
                        <td className="px-3 py-2.5 max-w-[220px]">
                          {p.url ? (
                            <a
                              href={p.url.startsWith('http') ? p.url : `https://${p.url}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary-700 hover:underline inline-flex items-center gap-1 truncate max-w-full"
                              title={p.url}
                            >
                              <span className="truncate">{p.url}</span>
                              <ExternalLink className="w-3 h-3 shrink-0" />
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{p.username || '—'}</td>
                        <td className="px-3 py-2.5 font-mono text-xs whitespace-nowrap">{p.password || '—'}</td>
                        <td className="px-3 py-2.5">
                          <div className="flex items-center justify-end gap-1">
                            <button
                              type="button"
                              title="Copy"
                              onClick={() => copyText(formatOne(p), `${displayName(p)} copied`)}
                              className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100 hover:text-slate-800"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Edit"
                              onClick={() => { setEditIndex(index); setFormOpen(true); }}
                              className="p-1.5 rounded-md text-primary-600 hover:bg-primary-50"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              type="button"
                              title="Delete"
                              disabled={saving}
                              onClick={() => handleDelete(index)}
                              className="p-1.5 rounded-md text-red-500 hover:bg-red-50 disabled:opacity-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t border-slate-200">
            <button type="button" onClick={onClose} className="btn-secondary">Close</button>
          </div>
        </div>
      </div>

      {formOpen && (
        <PortalFormPopup
          initial={editIndex != null ? portals[editIndex] : null}
          onClose={() => { setFormOpen(false); setEditIndex(null); }}
          onSave={handleFormSave}
        />
      )}
    </>
  );
};

export default PortalsAccessModal;
